import { DateTime } from 'luxon';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { getHourString, getMinuteString, ensureValidDateString, adjustDateString, isStartBeforeEndString, toCleanISO, addHour as addHourUtil, isTimeStringBiggerThan as isTimeStringBiggerThanUtil } from './time-string.utils';
import { getMinuteTimeDifference as getMinuteTimeDiffUtil, getHoursTimeDifferenceString as getHoursTimeDiffStringUtil, getTimeZones as getTimeZonesUtil } from './timezone.utils';
import { HourOption, getHourOptions as getHourOptionsUtil, getAllDayLabel, setAllDayLabel, getOpenEndLabel, setOpenEndLabel } from './hour-options.factory';
/** @deprecated legacy data */
interface LegacyData {
    date?: string; // date in jsonformat e.g.: 2024-03-26T23:00:00.000Z
    startHour?: string; // hour including minutes e.g. : 13:00
    endHour?: string; // hour including minutes e.g. : 13:00
}


export interface TimePeriodPersistanceData {
    /**
     * Start date and time
     * ISO 8601 date format without time zone e.g. "2025-04-20T14:30:00"
     * In case of all day it can be without time e.g. "2025-04-20"
     */
    "startString"?: string;

    /**
     * End date and time
     *  ISO 8601 date format without time zone e.g. "2025-04-20T15:30:00"
     * In case of all day it can be without time e.g. "2025-04-20"
     */
    "endString"?: string;

    /**
     * All day events are marked with an extra flag so it's more explicit.
     * If there is no such attribute it's not an all day event.
     * If the allDay flag is set the start and end date don't have to have a time - if they have a time it is ignored.
     */
    "allDay"?: boolean

    /**
     * Open end events only have a start time but no end time
     * If the openEnd flag is set the endString is ignored
     * Mutually exclusive with allDay
     */
    "openEnd"?: boolean

    /**
     * Multi-day events span more than one day
     * When set, startString and endString can have different dates
     * When not set or false, start and end are on the same day
     */
    "multiDay"?: boolean
}
export type TimePeriodData = TimePeriodPersistanceData | LegacyData
export { HourOption } from './hour-options.factory';

export class TimePeriod {
    /** Data to persist for TimePeriod
    * start-date-time, end-date-time, flags for all-day, open-end, and multi-day periods
     */
    private _data: TimePeriodPersistanceData = {};
    /**Private constructor to enforce the use of factory methods */
    private constructor() {
    }

    /*  FACTORY METHODS */

    /**
     * @returns a TimePeriod object that starts today at 12:00 and ends at 13:00
     */
    static newWithDefaultData(): TimePeriod {
        const newObject = new TimePeriod();
        newObject.startDateTime = DateTime.local().set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
        newObject.endDateTime = DateTime.local().set({ hour: 13, minute: 0, second: 0, millisecond: 0 });
        return newObject;
    }

    /**
     * Creates a new `TimePeriod` instance from the provided start and end date-time strings.
     * The method validates the input strings, adjusts them to a clean ISO format, and ensures
     * that the start date is before the end date. If start is after end, end is adjusted to match start.
     *
     * @param startString - The ISO 8601 string representing the start date-time.
     * @param endString - The ISO 8601 string representing the end date-time.
     * @param allDay - Optional flag indicating whether the time period spans the entire day. Mutually exclusive with openEnd.
     * @param openEnd - Optional flag indicating the event has no end time. Mutually exclusive with allDay and multiDay.
     * @param multiDay - Optional flag indicating the event spans multiple days. Mutually exclusive with openEnd.
     * @returns A new `TimePeriod` instance with the specified start and end times.
     * @throws {Error} If the provided date-time strings are invalid.
     */
    static newFromStrings(startString: string, endString: string, allDay?: boolean, openEnd?: boolean, multiDay?: boolean): TimePeriod {
        const newObject = new TimePeriod();
        TimePeriod.ensureValidDateString(startString)
        TimePeriod.ensureValidDateString(endString)
        startString = TimePeriod.adjustDateString(startString)
        endString = TimePeriod.adjustDateString(endString)
        if (!TimePeriod.isStartBeforeEndString(startString, endString)) {
            endString = startString
        }
        newObject._data.startString = startString
        newObject._data.endString = endString
        newObject.isAllDay = allDay
        if (openEnd) {
            newObject.isOpenEnd = true
        }
        if (multiDay) {
            newObject.isMultiDay = true
        }
        return newObject;
    }

    /**
     *
     * @param data
     * @returns if no data is provided returns TimePeriod object with default data
     */
    static newFromTimePeriodData(data: TimePeriodData): TimePeriod {
        if (!data) return TimePeriod.newWithDefaultData()
        if (isLegacyData(data)) {
            return newFromLegacyData(<LegacyData>data)
        } else {
            return newFromNewData(<TimePeriodPersistanceData>data)
        }

        /*  Inner function(s) */
        function isLegacyData(data: TimePeriodData): boolean {
            if ((<TimePeriodPersistanceData>data).startString) return false
            return true
        }

        function newFromNewData(data: TimePeriodPersistanceData): TimePeriod {
            const newObject = new TimePeriod();
            newObject._data = cloneDeep(data)
            return newObject;
        }


        function newFromLegacyData(data: LegacyData): TimePeriod {
            const newObject = new TimePeriod();
            if (!data.date) { return TimePeriod.newWithDefaultData() }
            const date: DateTime = DateTime.fromISO(data.date);
            if (isAllDay()) {
                newObject._data.allDay = true;
                newObject._data.startString = date.toISODate()
                newObject._data.endString = newObject._data.startString
            } else { // not allDay
                newObject._data.allDay = false;
                const startDateTime = date.set({
                    hour: parseInt(data.startHour.split(':')[0], 10),
                    minute: parseInt(data.startHour.split(':')[1], 10),
                });
                newObject._data.startString = TimePeriod.toCleanISO(startDateTime);
                const endDateTime = date.set({
                    hour: parseInt(data.endHour.split(':')[0], 10),
                    minute: parseInt(data.endHour.split(':')[1], 10),
                });
                newObject._data.endString = TimePeriod.toCleanISO(endDateTime);
            }
            return newObject;

            /*  Inner function(s) */
            function isAllDay(): boolean {
                {
                    return (data.startHour === '') || (data.endHour === '');
                }
            }
        }
    }

    static newFromJSDate(jsDate: Date, startHour?: string, endHour?: string): TimePeriod {
        const newObject = new TimePeriod();
        if (!jsDate) jsDate = new Date()
        if (!startHour || !endHour) setDefaultTime()
        const luxonDateTime = DateTime.fromJSDate(jsDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        if (isAllDay()) {
            newObject._data.allDay = true;
            newObject.startDateTime = luxonDateTime
            newObject.endDateTime = luxonDateTime
        } else { // not allDay
            const startDateTime = luxonDateTime.set({
                hour: parseInt(startHour.split(':')[0], 10),
                minute: parseInt(startHour.split(':')[1], 10),
            });
            const endDateTime = luxonDateTime.set({
                hour: parseInt(endHour.split(':')[0], 10),
                minute: parseInt(endHour.split(':')[1], 10),
            });
            newObject._data.endString = TimePeriod.toCleanISO(endDateTime);
            newObject._data.startString = TimePeriod.toCleanISO(startDateTime);
        }
        return newObject

        /*  Inner function(s) */
        function setDefaultTime() {
            if (!startHour) startHour = '12:00';
            if (!endHour) {
                endHour = TimePeriod.addHour(startHour)
            }
        }
        function isAllDay(): boolean { return (startHour === '') || (endHour === ''); }
    }

    /* GETTERS AND SETTERS - Flags */

    get isAllDay(): boolean {
        if (this._data.allDay) return true;
        return false
    }
    set isAllDay(value: boolean) {
        if (value) {
            this.startDateTime = this.startDateTime.set({ minute: 0, hour: 0, second: 0 })
            this.endDateTime = this.endDateTime.set({ minute: 0, hour: 0, second: 0 })
            this._data.allDay = true
            this._data.openEnd = false
        } else {

            this._data.allDay = false
            if (!this.startTimeString || !this.endTimeString) {
                // set default time while preserving dates
                this.startDateTime = this.startDateTime.set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
                this.endDateTime = this.endDateTime.set({ hour: 13, minute: 0, second: 0, millisecond: 0 });
            }
        }
    }

    get isOpenEnd(): boolean {
        if (this._data.openEnd) return true;
        return false
    }
    set isOpenEnd(value: boolean) {
        if (value) {
            this._data.openEnd = true
            this._data.allDay = false
            this._data.multiDay = false
            // Preserve end date, set end time to start time
            const endDt = this.endDateTime
            const startDt = this.startDateTime
            this._data.endString = TimePeriod.toCleanISO(
                endDt.set({ hour: startDt.hour, minute: startDt.minute, second: 0 })
            )
        } else {
            this._data.openEnd = false
            const endDt = this.endDateTime
            const startDt = this.startDateTime
            this._data.endString = TimePeriod.toCleanISO(
                endDt.set({ hour: startDt.hour, minute: startDt.minute, second: 0 })
            )
        }
    }

    get isMultiDay(): boolean {
        return this._data.multiDay === true
    }
    set isMultiDay(value: boolean) {
        if (value) {
            this._data.multiDay = true
            this._data.openEnd = false
        } else {
            this._data.multiDay = false
            // Reset end date to start date, preserve end time
            const startDt = this.startDateTime
            const endDt = this.endDateTime
            this._data.endString = TimePeriod.toCleanISO(
                endDt.set({ year: startDt.year, month: startDt.month, day: startDt.day })
            )
        }
    }

    private updateMultiDayFlag() {
        if (this.startDateString && this.endDateString) {
            this._data.multiDay = this.startDateString !== this.endDateString
        }
    }

    /* GETTERS AND SETTERS - Start/End Strings (ISO 8601) */

    /**
     * Gets the start date and time as a string.
     * ISO 8601 date format without time zone, e.g., "2025-04-20T14:30:00".
     * If the event is marked as all-day, it may return only the date, e.g., "2025-04-20".
     * @returns {string} The start date and time in string format.
     */
    get startString(): string {
        return this._data.startString
    }
    /**
     * Sets the start date string for the time period.
     * This method ensures that the provided date string is valid, adjusts it if necessary,
     * and verifies that the start date is before the end date. If start is after end,
     * the end date string is adjusted to match.
     *
     * @param value - The new start date string to be set.
     * @throws {Error} If the provided date string is invalid.
     */
    set startString(value: string) {
        TimePeriod.ensureValidDateString(value)
        value = TimePeriod.adjustDateString(value)
        this._data.startString = value
        if (!TimePeriod.isStartBeforeEndString(value, this.endString)) {
            this._data.endString = value
        }
        this.updateMultiDayFlag()
    }

    /**
     * Gets the end date and time as a string.
     * ISO 8601 date format without time zone, e.g., "2025-04-20T14:30:00".
     * If the event is marked as all-day, it may (!) return only the date, e.g., "2025-04-20".
     * @returns {string} The end date and time in string format.
     */
    get endString(): string {
        return this._data.endString
    }

    set endString(value: string) {
        TimePeriod.ensureValidDateString(value)
        value = TimePeriod.adjustDateString(value)
        this._data.endString = value
        if (!TimePeriod.isStartBeforeEndString(this._data.startString, this._data.endString)) {
            this._data.startString = value
        }
        this.updateMultiDayFlag()
    }

    /* GETTERS AND SETTERS - Start/End DateTime (Luxon) */

    /**
     * Returns a Luxon `DateTime` object representing the start date and time.
     *
     * - The returned `DateTime` object is created from the `_data.startString` property.
     * - The returned object is independent of the internal state, so modifying it will not affect the `TimePeriod` instance.
     *
     * @returns {DateTime} A Luxon `DateTime` object representing the start date and time.
     */
    get startDateTime(): DateTime {
        return DateTime.fromISO(this._data.startString);
    }

    /**
     * Sets the start date and time for the time period.
     *
     * - If the new start date-time is later than the current end date-time, the end date-time is automatically updated to match the new start date-time.
     *
     * @param {DateTime} newValue - The new start date and time as a Luxon `DateTime` object.
     */
    set startDateTime(newValue: DateTime) {
        this._data.startString = TimePeriod.toCleanISO(newValue);
        if (newValue > this.endDateTime) {
            this._data.endString = this._data.startString;
        }
        this.updateMultiDayFlag()
    }

    get endDateTime(): DateTime {
        return DateTime.fromISO(this._data.endString)
    }

    set endDateTime(newValue: DateTime) {
        this._data.endString = TimePeriod.toCleanISO(newValue);
        if (!TimePeriod.isStartBeforeEndString(this._data.startString, this._data.endString)) {
            this._data.startString = this._data.endString
        }
        this.updateMultiDayFlag()
    }

    /* GETTERS AND SETTERS - Time-only Strings ("HH:mm") */

    /**
     * Retrieves the formatted start time as a string e.g : "11:00"
     * If the time period is marked as "all day," an empty string is returned.
     * Otherwise, the start time is formatted as "HH:mm" using the hour and minute
     * extracted from the `startDateTime` property.
     *
     * @returns {string} The formatted start time string or an empty string if it's an all-day event.
     */
    get startTimeString(): string {
        if (this.isAllDay) return ""
        return TimePeriod.getHourString(this.startDateTime) + ":" + TimePeriod.getMinuteString(this.startDateTime)
    }

    /**
     * Sets the start time of the time period using a string in the format "HH:mm".
     *
     * - If the provided value is an empty string, the `isAllDay` flag is set to `true`.
     * - If `isAllDay` is `true` and a non-empty value is provided, the `isAllDay` flag is set to `false`
     *   and the `endDateTime` is also updated to match the new start time.
     * - For multi-day periods with different start/end dates, start/end time adjustment is skipped.
     * - Validates the input format and ensures the hour is between 0-23 and the minute is between 0-59.
     * - Updates the `startDateTime` property with the new time while keeping the date unchanged.
     *
     * @param value - The new start time in the format "HH:mm". An empty string sets the `isAllDay` flag to `true`.
     * @throws {Error} If the provided value is not in the correct "HH:mm" format or contains invalid hour/minute values.
     */
    set startTimeString(value: string) {
        if (!value) {
            this.isAllDay = true // also resets openEnd
        } else {
            try {
                const [hour, minute] = value.split(':').map(Number);
                if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
                    throw new Error('Invalid time format');
                }
                if (this.isMultiDay && this.startDateString !== this.endDateString) {
                    const wasAllDay = this.isAllDay;
                    // Different dates: skip start/end adjustment
                    this._data.startString = TimePeriod.toCleanISO(this.startDateTime.set({ hour, minute }));
                    if (wasAllDay) {
                        this._data.allDay = false;
                    }
                } else {
                    this.startDateTime = this.startDateTime.set({ hour, minute });
                    if (this.isAllDay) {
                        this.isAllDay = false;
                        this.endDateTime = this.endDateTime.set({ hour, minute });
                    }
                }
            } catch (e) {
                throw new Error('Error setting startTimeString; value: ' + value + '; Error: ' + e.message);
            }
        }
    }

    /**
     * Retrieves the formatted end time as a string e.g. "12:00".
     * If the time period is marked as "all day" or "open end", an empty string is returned.
     * Otherwise, the end time is formatted as "HH:mm" using the hour and minute
     * extracted from the `endDateTime` property.
     *
     * @returns {string} The formatted end time string or an empty string if it's an all-day or open-end event.
     */
    get endTimeString(): string {
        if (this.isAllDay) return ""
        if (this.isOpenEnd) return ""
        return TimePeriod.getHourString(this.endDateTime) + ":" + TimePeriod.getMinuteString(this.endDateTime)
    }

    /**
     * Sets the end time of the time period using a string in the format "HH:mm".
     *
     * - If the provided value is an empty string and `isAllDay` is already `true`, no change is made.
     * - If the provided value is an empty string and `isAllDay` is `false`, the `isOpenEnd` flag is set to `true`.
     * - If `isAllDay` is `true` and a non-empty value is provided, the `isAllDay` flag is set to `false`
     *   and the `startDateTime` is also updated to match the new end time.
     * - For multi-day periods with different start/end dates, start/end time adjustment is skipped.
     * - Validates the input format and ensures the hour is between 0-23 and the minute is between 0-59.
     * - Updates the `endDateTime` property with the new time while keeping the date unchanged.
     *
     * @param value - The new end time in the format "HH:mm". An empty string is a no-op when already all-day,
     *   or sets `isOpenEnd` to `true` otherwise.
     * @throws {Error} If the provided value is not in the correct "HH:mm" format or contains invalid hour/minute values.
     */
    set endTimeString(value: string) {
        if (!value && this.isAllDay) {
            return; // already all day, no change needed
        } else if (!value) {
            this.isOpenEnd = true
        } else {
            if (this.isOpenEnd) {
                this.isOpenEnd = false
            }
            const current = this.endDateTime;
            try {
                const [hour, minute] = value.split(':').map(Number);
                if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
                    throw new Error('Invalid time format');
                }
                if (this.isMultiDay && this.startDateString !== this.endDateString) {
                    const wasAllDay = this.isAllDay;
                    // Different dates: skip start/end adjustment
                    this._data.endString = TimePeriod.toCleanISO(current.set({ hour, minute }));
                    if (wasAllDay) {
                        this._data.allDay = false;
                    }
                } else {
                    if (this.isAllDay) {
                        this.endDateTime = current.set({ hour, minute });
                        this.isAllDay = false;
                        this.startDateTime = this.startDateTime.set({ hour, minute });
                    } else {
                        this.endDateTime = current.set({ hour, minute });
                    }
                }
            } catch (e) {
                throw new Error('Error setting endTimeString; value: ' + value + '; Error: ' + e.message);
            }
        }
    }

    /* GETTERS AND SETTERS - Date-only Strings ("YYYY-MM-DD") */

    /**
     * Gets the date-only portion of the start value as a string.
     * @returns The start date in "YYYY-MM-DD" format when available, or undefined if startString is not set.
     */
    get startDateString(): string {
        if (this.startString) {
            const tIndex = this.startString.indexOf('T');
            if (tIndex > -1) {
                return this.startString.substring(0, tIndex);
            }
            return this.startString; // already just a date
        }
    }

    /**
     * Gets the end date as a date-only string in the format YYYY-MM-DD.
     *
     * @returns The date-only representation of the end value, or undefined if unavailable.
     *
     */
    get endDateString(): string {
        if (this.endString) {
            const tIndex = this.endString.indexOf('T');
            if (tIndex > -1) {
                return this.endString.substring(0, tIndex);
            }
            return this.endString; // already just a date
        }
    }

    /* GETTERS - Persistence Data */

    /**
     * Retrieves a deep-cloned copy of the internal time period data.
     * This ensures that the returned data is immutable and cannot be
     * modified externally, preserving the integrity of the original data.
     *
     * @returns {TimePeriodPersistanceData} A deep-cloned copy of the time period data.
     */
    get timePeriodData(): TimePeriodPersistanceData {
        return cloneDeep(this._data)
    }

    /* INSTANCE METHODS */

    /**
     * Changes both start and end date - the times remain untouched.
     * @param date - The new date for both start and end. Defaults to today if not provided.
     */
    setStartAndEndDate(date: Date) {
        if (!date) date = new Date() // default is today
        const newObject = TimePeriod.newFromJSDate(date, this.startTimeString, this.endTimeString)
        this.copy(newObject)
        this.updateMultiDayFlag()
    }

    /**
     * Changes only the start date, preserving the start time.
     * If the new start date is after the end date, the end date is adjusted to match.
     */
    setStartDate(date: Date) {
        if (!date) date = new Date()
        const luxonDate = DateTime.fromJSDate(date)
        const newStart = this.startDateTime.set({
            year: luxonDate.year,
            month: luxonDate.month,
            day: luxonDate.day,
        })
        this.startDateTime = newStart
        // startDateTime setter already adjusts end if start > end
    }

    /**
     * Changes only the end date, preserving the end time.
     * If the new end date is before the start date, the start date is adjusted to match.
     */
    setEndDate(date: Date) {
        if (!date) date = new Date()
        const luxonDate = DateTime.fromJSDate(date)
        const newEnd = this.endDateTime.set({
            year: luxonDate.year,
            month: luxonDate.month,
            day: luxonDate.day,
        })
        this._data.endString = TimePeriod.toCleanISO(newEnd)
        if (!TimePeriod.isStartBeforeEndString(this._data.startString, this._data.endString)) {
            this._data.startString = this._data.endString
        }
        this.updateMultiDayFlag()
    }

    /**
     * Copies the date components (year, month, and day) from the `startDateTime` and `endDateTime`
     * of the provided `TimePeriod` instance to the current instance's `startDateTime` and `endDateTime`.
     *
     * @param toCopyFrom - The `TimePeriod` instance from which to copy the date components.
     */
    copyDates(toCopyFrom: TimePeriod) {
        const startDateTimeToCopy = toCopyFrom.startDateTime;
        const endDateTimeToCopy = toCopyFrom.endDateTime;
        // Read both current values upfront before any mutations
        const currentStartDateTime = this.startDateTime;
        const currentEndDateTime = this.endDateTime;
        // Write directly to _data to avoid setter side effects
        this._data.startString = TimePeriod.toCleanISO(currentStartDateTime.set({
            year: startDateTimeToCopy.year,
            month: startDateTimeToCopy.month,
            day: startDateTimeToCopy.day,
        }));
        this._data.endString = TimePeriod.toCleanISO(currentEndDateTime.set({
            year: endDateTimeToCopy.year,
            month: endDateTimeToCopy.month,
            day: endDateTimeToCopy.day,
        }));
        this.updateMultiDayFlag()
    }

    /**
     * Copies all properties from the provided `TimePeriod` instance into the current instance.
     * This performs a deep copy, ensuring that nested properties are also duplicated.
     *
     * @param toCopyFrom - The `TimePeriod` instance to copy data from.
     */
    copy(toCopyFrom: TimePeriod) {
        merge(this, toCopyFrom);// deep copy all properties
    }

    /**
     * Creates a deep copy of the current `TimePeriod` instance.
     * This ensures that all properties, including nested objects, are duplicated,
     * and changes to the cloned instance do not affect the original instance.
     *
     * @returns A new `TimePeriod` instance that is a deep copy of the current instance.
     */
    public clone(): TimePeriod {
        return cloneDeep(this)
    }

    /**
     * Returns a human-readable string representation of the time period using the browser locale.
     *
     * Formats:
     * - Single day:       "Apr 20; 14:00 - 16:00"
     * - Single day allDay: "Apr 20; ALL DAY"
     * - Single day openEnd: "Apr 20; 14:00"
     * - Multi-day:        "Apr 20; 14:00 - Apr 22; 16:00"
     * - Multi-day allDay:  "Apr 20 - Apr 22; ALL DAY"
     *
     * @returns {string} A locale-formatted string describing the time period.
     */
    toString(): string {
        const locale = navigator.language || 'en'
        const dateFormat = { month: 'short' as const, day: '2-digit' as const }
        const startDate = this.startDateTime.setLocale(locale).toLocaleString(dateFormat)

        if (this.isMultiDay) {
            const endDate = this.endDateTime.setLocale(locale).toLocaleString(dateFormat)
            if (this.isAllDay) {
                return `${startDate} - ${endDate}; ${TimePeriod.getAllDayLabel()}`
            } else {
                return `${startDate}; ${this.startTimeString} - ${endDate}; ${this.endTimeString}`
            }
        }

        let time: string
        if (this.isAllDay) {
            time = TimePeriod.getAllDayLabel()
        } else if (this.isOpenEnd) {
            time = this.startTimeString;
        } else {
            time = `${this.startTimeString} - ${this.endTimeString}`;
        }
        return startDate + '; ' + time
    }

    /* STATIC DELEGATORS - Comparison */

    /**
     * Compares start date and time
     * @param timePeriodA
     * @param timePeriodB
     * @returns  -1 :  if start of timePeriodA  <    start of timePeriodB
     *            1 :  if start of timePeriodA  >    start of timePeriodB
     *            0 :  if start of timePeriodA ===   start of timePeriodB
     */
    static compareStartDateTime(timePeriodA: TimePeriod, timePeriodB: TimePeriod): 1 | 0 | -1 {
        const startA = timePeriodA.startDateTime
        const startB = timePeriodB.startDateTime
        if (startA < startB) {
            return -1;
        } else if (startA > startB) {
            return 1;
        } else {
            return 0;
        }
    }

    /* STATIC DELEGATORS - Time String Utilities */

    /**
     * Returns the hour as a zero-padded two-digit string: "00", "01", ... "22", "23".
     */
    static getHourString(dateTime: DateTime): string {
        return getHourString(dateTime);
    }

    /**
     * Adds one hour to a time string.
     * @param hourString - Format "HH:mm", e.g. "13:15"
     * @returns e.g. "14:15". Caps at "23:mm".
     */
    static addHour(hourString: string): string {
        return addHourUtil(hourString);
    }

    /**
     * @returns true if hour1 > hour2, false otherwise.
     */
    static isTimeStringBiggerThan(hour1: string, hour2: string): boolean {
        return isTimeStringBiggerThanUtil(hour1, hour2);
    }

    /* STATIC DELEGATORS - Labels & Hour Options */

    static setAllDAyLabel(label: string): void {
        setAllDayLabel(label);
    }

    static getAllDayLabel(): string {
        return getAllDayLabel();
    }

    static setOpenEndLabel(label: string): void {
        setOpenEndLabel(label);
    }

    static getOpenEndLabel(): string {
        return getOpenEndLabel();
    }

    static getHourOptions(): { startHourOptions: HourOption[], endHourOptions: HourOption[] } {
        return getHourOptionsUtil();
    }

    /* STATIC DELEGATORS - Timezone */

    /**
     * Calculates the time difference in minutes between the provided time zone and the local time zone.
     *
     * @param timeZone - The IANA time zone name to compare with the local time zone.
     * @returns The time difference in minutes. Positive if the provided time zone is ahead of the local time zone, negative if behind.
     */
    static getMinuteTimeDifference(timeZone: string): number {
        return getMinuteTimeDiffUtil(timeZone);
    }

    /**
     * Calculates the time difference in hours between the provided time zone and the local time zone.
     *
     * @param timeZone - The IANA time zone name to compare with the local time zone.
     * @returns A string representing the time difference in hours.
     *          Positive values are prefixed with "+", negative values  with a "-"
     */
    static getHoursTimeDifferenceString(timeZone: string): string {
        return getHoursTimeDiffStringUtil(timeZone);
    }

    static getTimeZones() {
        return getTimeZonesUtil()
    }

    /* PRIVATE STATIC DELEGATORS */

    private static getMinuteString(dateTime: DateTime): string {
        return getMinuteString(dateTime);
    }

    private static toCleanISO(dateTime: DateTime): string {
        return toCleanISO(dateTime);
    }

    private static ensureValidDateString(dateString: string): void {
        ensureValidDateString(dateString);
    }

    private static adjustDateString(dateString: string): string {
        return adjustDateString(dateString);
    }

    private static isStartBeforeEndString(startString: string, endString: string): boolean {
        return isStartBeforeEndString(startString, endString);
    }
}
